import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

export interface Author {
  id: string;
  name: string;
  avatar: string;
  location: string;
}

export interface Comment {
  id: string;
  discussionId: string;
  content: string;
  author: Author;
  date: string;
}

export interface Discussion {
  id: string;
  title: string;
  content: string;
  author: Author;
  date: string;
  tags: string[];
  likes: number;
  comments: number;
  views: number;
  isLiked?: boolean; // Optimistic UI state
}

interface CommunityContextType {
  discussions: Discussion[];
  savedPosts: string[]; // IDs of saved posts
  addDiscussion: (discussion: Omit<Discussion, 'id' | 'date' | 'likes' | 'comments' | 'views' | 'author' | 'isLiked'>) => Promise<void>;
  toggleSavePost: (postId: string) => Promise<void>;
  toggleLikePost: (postId: string) => Promise<void>;
  getDiscussionById: (id: string) => Discussion | undefined;
  getComments: (discussionId: string) => Promise<Comment[]>;
  addComment: (discussionId: string, content: string) => Promise<void>;
  loading: boolean;
  refreshDiscussions: () => Promise<void>;
}

const CommunityContext = createContext<CommunityContextType | undefined>(undefined);

export const useCommunity = () => {
  const context = useContext(CommunityContext);
  if (!context) {
    throw new Error('useCommunity must be used within a CommunityProvider');
  }
  return context;
};

// Initial Mock Data (Fallback)
const mockDiscussions: Discussion[] = [
  {
    id: '1',
    title: 'Best practices for maize farming in dry regions',
    content: 'I\'ve been struggling with my maize farm due to the recent drought. Does anyone have experience with drought-resistant varieties or water conservation techniques that work well in Kenya?',
    author: {
      id: 'mock-1',
      name: 'John Kamau',
      avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
      location: 'Machakos',
    },
    date: '2023-04-15T10:30:00',
    tags: ['Maize', 'Drought', 'Water Conservation'],
    likes: 24,
    comments: 8,
    views: 156,
  },
  // ... (Other mocks would be here, kept minimal for fallback)
];

export const CommunityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [savedPosts, setSavedPosts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchDiscussions = async () => {
    try {
      setLoading(true);
      // Fetch discussions with author details
      const { data, error } = await supabase
        .from('discussions')
        .select(`
          *,
          author:profiles(id, name, county),
          likes:discussion_likes(count),
          comments:discussion_comments(count)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        // Fetch user's likes and saved posts to set initial state
        let userLikes: string[] = [];
        let userSaved: string[] = [];

        if (user) {
          const [likesResult, savedResult] = await Promise.all([
            supabase.from('discussion_likes').select('discussion_id').eq('user_id', user.id),
            supabase.from('saved_discussions').select('discussion_id').eq('user_id', user.id)
          ]);

          if (likesResult.data) userLikes = likesResult.data.map(l => l.discussion_id);
          if (savedResult.data) userSaved = savedResult.data.map(s => s.discussion_id);
          setSavedPosts(userSaved);
        }

        const formattedDiscussions: Discussion[] = data.map((d: any) => ({
          id: d.id,
          title: d.title,
          content: d.content,
          author: {
            id: d.author?.id || 'unknown',
            name: d.author?.name || 'Anonymous',
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(d.author?.name || 'Anonymous')}`,
            location: d.author?.county || 'Kenya',
          },
          date: d.created_at,
          tags: d.tags || [],
          likes: d.likes?.[0]?.count || 0,
          comments: d.comments?.[0]?.count || 0,
          views: d.views || 0,
          isLiked: userLikes.includes(d.id)
        }));
        setDiscussions(formattedDiscussions);
      }
    } catch (error) {
      console.error('Error fetching discussions:', error);
      // Fallback to local/mock if empty or error (optional, mostly for dev)
      if (discussions.length === 0) {
        // Only load mocks if we really have nothing
        // setDiscussions(mockDiscussions); 
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscussions();
  }, [user]); // Refetch when user changes (login/logout)

  const addDiscussion = async (newDiscussionData: Omit<Discussion, 'id' | 'date' | 'likes' | 'comments' | 'views' | 'author' | 'isLiked'>) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('discussions')
        .insert({
          title: newDiscussionData.title,
          content: newDiscussionData.content,
          tags: newDiscussionData.tags,
          author_id: user.id
        })
        .select()
        .single();

      if (error) throw error;

      // Optimistic update or refetch
      await fetchDiscussions();
    } catch (error) {
      console.error('Error adding discussion:', error);
    }
  };

  const toggleSavePost = async (postId: string) => {
    if (!user) return;

    // Optimistic update
    const isSaved = savedPosts.includes(postId);
    setSavedPosts(prev => isSaved ? prev.filter(id => id !== postId) : [...prev, postId]);

    try {
      if (isSaved) {
        await supabase.from('saved_discussions').delete().eq('discussion_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('saved_discussions').insert({ discussion_id: postId, user_id: user.id });
      }
    } catch (error) {
      console.error('Error toggling saved post:', error);
      // Revert on error
      setSavedPosts(prev => isSaved ? [...prev, postId] : prev.filter(id => id !== postId));
    }
  };

  const toggleLikePost = async (postId: string) => {
    if (!user) return;

    const discussion = discussions.find(d => d.id === postId);
    if (!discussion) return;

    const isLiked = discussion.isLiked;

    // Optimistic update
    setDiscussions(prev => prev.map(d => {
      if (d.id === postId) {
        return {
          ...d,
          likes: isLiked ? d.likes - 1 : d.likes + 1,
          isLiked: !isLiked
        };
      }
      return d;
    }));

    try {
      if (isLiked) {
        await supabase.from('discussion_likes').delete().eq('discussion_id', postId).eq('user_id', user.id);
      } else {
        await supabase.from('discussion_likes').insert({ discussion_id: postId, user_id: user.id });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert
      setDiscussions(prev => prev.map(d => {
        if (d.id === postId) {
          return {
            ...d,
            likes: isLiked ? d.likes + 1 : d.likes - 1,
            isLiked: isLiked
          };
        }
        return d;
      }));
    }
  };

  const getDiscussionById = (id: string) => {
    return discussions.find(d => d.id === id);
  };

  const getComments = async (discussionId: string): Promise<Comment[]> => {
    try {
      const { data, error } = await supabase
        .from('discussion_comments')
        .select(`
          id,
          content,
          created_at,
          author:profiles(id, name, county)
        `)
        .eq('discussion_id', discussionId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return data.map((c: any) => ({
        id: c.id,
        discussionId,
        content: c.content,
        author: {
          id: c.author?.id || 'unknown',
          name: c.author?.name || 'Anonymous',
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(c.author?.name || 'Anonymous')}`,
          location: c.author?.county || 'Kenya',
        },
        date: c.created_at
      }));
    } catch (error) {
      console.error('Error fetching comments:', error);
      return [];
    }
  };

  const addComment = async (discussionId: string, content: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('discussion_comments')
        .insert({
          discussion_id: discussionId,
          user_id: user.id,
          content
        });

      if (error) throw error;
      
      // Update local discussion comment count
      setDiscussions(prev => prev.map(d => 
        d.id === discussionId 
          ? { ...d, comments: d.comments + 1 }
          : d
      ));
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  return (
    <CommunityContext.Provider value={{ 
      discussions, 
      savedPosts, 
      addDiscussion, 
      toggleSavePost, 
      toggleLikePost,
      getComments,
      addComment,
      getDiscussionById,
      loading,
      refreshDiscussions: fetchDiscussions
    }}>
      {children}
    </CommunityContext.Provider>
  );
};
